import React from 'react';
import { Button } from '../ui/button';
import { MessageSquare, Trash2, Loader2 } from 'lucide-react';
import { SessionListProps } from '../../types/components';
import { formatRelativeTime } from '../../utils';

// Memoized session item component for better performance
const SessionItem: React.FC<{
  session: any;
  currentSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onDeleteSession?: (sessionId: string) => void;
  disabled?: boolean;
  disabledReason?: string;
}> = React.memo(({ session, currentSessionId, onSessionSelect, onDeleteSession, disabled = false, disabledReason }) => {
  const handleDeleteSession = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteSession && window.confirm('Are you sure you want to delete this chat session? This action cannot be undone.')) {
      onDeleteSession(session.id);
    }
  };

  const handleSelect = () => {
    if (disabled) {
      return; // Simply do nothing when disabled
    }
    onSessionSelect(session.id);
  };

  const isCurrentSession = currentSessionId === session.id;

  return (
    <div
      className={`relative group p-3 rounded-card transition-colors border ${
        isCurrentSession
          ? 'bg-rm-primary-700/10 border-rm-primary-700/30 shadow-soft'
          : disabled
          ? 'bg-rm-bg-50 border-rm-neutral-600/20 cursor-not-allowed opacity-50'
          : 'hover:bg-rm-bg-50 border-transparent cursor-pointer'
      }`}
      onClick={handleSelect}
      title={disabled ? disabledReason : undefined}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h3 className={`font-medium text-sm truncate ${
            disabled ? 'text-rm-neutral-600' : 'text-rm-ink-900'
          }`}>
            {session.title || `Session ${session.id.slice(-8)}`}
          </h3>
          <p className="text-xs text-rm-neutral-600 mt-1">
            {formatRelativeTime(session.updatedAt)}
          </p>
        </div>
        
        {onDeleteSession && !disabled && (
          <Button
            size="sm"
            variant="ghost"
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-6 w-6"
            onClick={handleDeleteSession}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
      
      {session.metadata && (session.metadata as any).lastMessage && (
        <p className={`text-xs mt-2 truncate ${
          disabled ? 'text-rm-neutral-600/60' : 'text-rm-neutral-600'
        }`}>
          {(session.metadata as any).lastMessage}
        </p>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if relevant props have changed
  return (
    prevProps.session.id === nextProps.session.id &&
    prevProps.session.title === nextProps.session.title &&
    prevProps.session.updatedAt === nextProps.session.updatedAt &&
    prevProps.currentSessionId === nextProps.currentSessionId &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.disabledReason === nextProps.disabledReason &&
    JSON.stringify(prevProps.session.metadata) === JSON.stringify(nextProps.session.metadata)
  );
});

export const SessionList: React.FC<SessionListProps> = React.memo(({
  sessions,
  currentSessionId,
  onSessionSelect,
  onDeleteSession,
  disabled = false,
  disabledReason = "Please wait for the current response to complete before switching sessions",
  hasMore = false,
  onLoadMore,
  isLoadingMore = false
}) => {
  return (
    <div className="flex-1 overflow-y-auto">
      {disabled && (
        <div className="p-2 mx-2 mb-2 bg-rm-bg-tint border border-rm-accent-300/30 rounded-card">
          <p className="text-xs text-rm-primary-700 text-center">
            {disabledReason}
          </p>
        </div>
      )}
      {sessions.length === 0 && !isLoadingMore ? (
        <div className="p-4 text-center text-rm-neutral-600">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 text-rm-neutral-600/50" />
          <p className="text-sm">No chat sessions yet</p>
        </div>
      ) : (
        <div className="space-y-1 p-2">
          {sessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              currentSessionId={currentSessionId || null}
              onSessionSelect={onSessionSelect}
              onDeleteSession={onDeleteSession}
              disabled={disabled}
              disabledReason={disabledReason}
            />
          ))}
          
          {/* Load More Button */}
          {hasMore && onLoadMore && (
            <div className="p-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={onLoadMore}
                disabled={disabled || isLoadingMore}
                className="w-full"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading more...
                  </>
                ) : (
                  'Load More Sessions'
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if sessions array, current session, disabled state, or pagination props change
  return (
    prevProps.sessions.length === nextProps.sessions.length &&
    prevProps.currentSessionId === nextProps.currentSessionId &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.hasMore === nextProps.hasMore &&
    prevProps.isLoadingMore === nextProps.isLoadingMore &&
    prevProps.sessions.every((session, index) => 
      session.id === nextProps.sessions[index]?.id &&
      session.updatedAt === nextProps.sessions[index]?.updatedAt
    )
  );
});